import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { usePreferencesStore } from '../../store/preferencesStore';
import { resolveShortcutKeys, shortcutTokens } from '../../config/shortcuts';
import { SearchGlyph, CloseGlyph } from './treeIcons';

/**
 * The search box under the header (card 1.3): 32 px, search glyph left, and on
 * the right either the command palette's shortcut as a kbd chip ("Ctrl K" —
 * the user's own binding, from the shortcut registry) or, while there is text,
 * a clear button. Typing filters the tree below (the section does that);
 * Escape clears and hands focus back to the tree (`onEscape`). Enter goes to
 * `onSubmit`, which returns true when it used the text (a pasted document
 * link opens its page, card 5.3).
 */
export default function SidebarSearch({ value, onChange, onEscape, onSubmit, inputRef }) {
  const { t } = useTranslation();
  const keyboardShortcuts = usePreferencesStore((s) => s.keyboardShortcuts);
  const paletteKeys = useMemo(
    () => shortcutTokens(resolveShortcutKeys('commandPalette', keyboardShortcuts)).join(' '),
    [keyboardShortcuts]
  );

  return (
    <div className="doc-search-wrap">
      <div className="doc-search">
        <SearchGlyph size={14} className="doc-search-icon" />
        <input
          ref={inputRef}
          type="text"
          placeholder={t('docSidebar.search.placeholder')}
          aria-label={t('docSidebar.search.placeholder')}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && onSubmit?.(value)) {
              e.preventDefault();
              return;
            }
            if (e.key !== 'Escape') return;
            e.preventDefault();
            e.stopPropagation();
            onChange('');
            onEscape?.();
          }}
          className="doc-search-input tab-dropdown-search-input"
          autoComplete="off"
          spellCheck={false}
        />
        {value ? (
          <button
            type="button"
            onClick={() => onChange('')}
            className="doc-search-clear"
            aria-label={t('docSidebar.search.clear')}
            onMouseDown={(e) => e.preventDefault()}
          >
            <CloseGlyph size={12} />
          </button>
        ) : paletteKeys ? (
          <kbd className="doc-search-kbd" aria-hidden="true">
            {paletteKeys}
          </kbd>
        ) : null}
      </div>
    </div>
  );
}
